import { useEffect, useMemo, useState } from 'react'
import { Plus, Check, ChevronLeft, ChevronRight } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { TopTabs } from '@/components/dashboard/TopTabs'
import {
  useKhCatalog,
  useKhCatalogFacets,
  useAddToShopify,
  type KhCatalogFilters,
  type KhSort,
} from '@/hooks/useKhCatalog'

const PAGE_SIZE = 50

function money(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`
}

// KH stock badge — green ≥10, amber 1–9, red 0 (brief §Feature 2).
// Zero is red, not amber: this is the dropship catalogue, so no KH stock is
// the exception that stops sourcing (owner decision, colour pass).
const PILL = 'inline-block rounded-full text-[10.5px] font-semibold px-2 py-0.5 leading-none'
function stockClasses(qty: number): string {
  if (qty <= 0) return 'bg-[var(--status-rejected-bg)] text-[var(--status-rejected-fg)]'
  if (qty < 10) return 'bg-[var(--status-awaiting-bg)] text-[var(--status-awaiting-fg)]'
  return 'bg-[var(--status-approved-bg)] text-[var(--status-approved-fg)]'
}

export default function Catalog() {
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [mainGroup, setMainGroup] = useState('')
  const [subGroup, setSubGroup] = useState('')
  const [season, setSeason] = useState('')
  const [inShopify, setInShopify] = useState<'all' | 'yes' | 'no'>('all')
  const [sort, setSort] = useState<KhSort>('newest')
  const [page, setPage] = useState(1)

  // Debounce the search box so we don't fire a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300)
    return () => clearTimeout(t)
  }, [search])

  // Any filter change resets to page 1 (stale offset would show an empty page).
  useEffect(() => {
    setPage(1)
  }, [debouncedSearch, mainGroup, subGroup, season, inShopify, sort])

  const filters: KhCatalogFilters = {
    search: debouncedSearch,
    main_group: mainGroup,
    sub_group: subGroup,
    season,
    in_shopify: inShopify === 'all' ? undefined : inShopify === 'yes',
    sort,
    page,
    page_size: PAGE_SIZE,
  }

  const { data, isLoading, isFetching } = useKhCatalog(filters)
  const facets = useKhCatalogFacets()
  const addToShopify = useAddToShopify()

  const items = data?.items ?? []
  const total = data?.total ?? 0
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))

  const pendingSku = addToShopify.isPending ? (addToShopify.variables as string) : null

  async function handleAdd(sku: string) {
    try {
      const res = await addToShopify.mutateAsync(sku)
      toast.success(
        res.status === 'already_in_shopify'
          ? `${sku} is already in Shopify`
          : `${sku} added to Shopify as a draft`
      )
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Add to Shopify failed')
    }
  }

  const selectCls =
    'h-7 text-xs border border-[var(--line-input)] bg-[var(--surface-card)] px-1 rounded-sm ' +
    'focus:outline-none focus:border-[var(--brand)]'

  const rangeLabel = useMemo(() => {
    if (total === 0) return '0'
    const start = (page - 1) * PAGE_SIZE + 1
    const end = Math.min(page * PAGE_SIZE, total)
    return `${start}–${end} of ${total}`
  }, [page, total])

  return (
    <div className="brand-theme flex flex-col h-screen bg-[var(--surface-page)] text-[var(--text-primary)]">
      <TopTabs />
      <div className="flex-1 overflow-auto p-4">
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <h1 className="text-base font-semibold">KH Catalog</h1>
          <span className="text-xs text-[var(--mute)]">{rangeLabel}</span>
          {isFetching && !isLoading && (
            <span className="text-xs text-[var(--mute)]">updating…</span>
          )}
        </div>

        {/* Filter bar */}
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name / SKU / EAN"
            className="h-7 text-xs border border-[var(--line-input)] bg-[var(--surface-card)] px-2 rounded-sm w-56 focus:outline-none focus:border-[var(--brand)]"
            aria-label="Search catalog"
          />
          <select
            value={mainGroup}
            onChange={(e) => setMainGroup(e.target.value)}
            className={selectCls}
            aria-label="Main group"
          >
            <option value="">All categories</option>
            {facets.data?.main_groups.map((g) => (
              <option key={g} value={g}>{g}</option>
            ))}
          </select>
          <select
            value={subGroup}
            onChange={(e) => setSubGroup(e.target.value)}
            className={selectCls}
            aria-label="Sub group"
          >
            <option value="">All sub-groups</option>
            {facets.data?.sub_groups.map((g) => (
              <option key={g} value={g}>{g}</option>
            ))}
          </select>
          <select
            value={season}
            onChange={(e) => setSeason(e.target.value)}
            className={selectCls}
            aria-label="Season"
          >
            <option value="">All seasons</option>
            {facets.data?.seasons.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
          <select
            value={inShopify}
            onChange={(e) => setInShopify(e.target.value as 'all' | 'yes' | 'no')}
            className={selectCls}
            aria-label="In Shopify filter"
          >
            <option value="all">All products</option>
            <option value="yes">In Shopify</option>
            <option value="no">Not in Shopify</option>
          </select>
          <div className="flex-1" />
          <label className="text-xs text-[var(--mute)]">Sort</label>
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value as KhSort)}
            className={selectCls}
            aria-label="Sort order"
          >
            <option value="newest">Newest</option>
            <option value="stock_desc">KH stock (high→low)</option>
            <option value="name_asc">Name (A→Z)</option>
          </select>
        </div>

        {isLoading ? (
          <div className="space-y-1">
            {Array.from({ length: 10 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}
          </div>
        ) : (
          <div className="overflow-auto border border-[var(--line-warm)] bg-[var(--surface-card)]">
            <table className="text-xs w-full">
              <thead className="bg-[var(--surface-header)] border-b border-[var(--line-warm)] sticky top-0">
                <tr className="text-[var(--mute)]">
                  <th className="tbl-th text-left w-12">Image</th>
                  <th className="tbl-th text-left">Name</th>
                  <th className="tbl-th text-left">SKU</th>
                  <th className="tbl-th text-left">Variant</th>
                  <th className="tbl-th text-left">EAN (Barcode)</th>
                  <th className="tbl-th text-right">KH price</th>
                  <th className="tbl-th text-right">KH stock</th>
                  <th className="tbl-th text-center">Season</th>
                  <th className="tbl-th text-center">In Shopify?</th>
                  <th className="tbl-th text-center">Action</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="px-2 py-6 text-center text-[var(--mute)]">
                      No catalog rows match these filters.
                    </td>
                  </tr>
                ) : (
                  items.map((it, i) => {
                    const variant = it.variant_description || [it.color, it.size].filter(Boolean).join(' / ')
                    const rowPending = pendingSku === it.sku
                    return (
                      <tr
                        key={it.sku}
                        className={`border-b border-[var(--line-warm)] ${
                          i % 2 === 1 ? 'bg-[var(--surface-zebra)]' : 'bg-[var(--surface-card)]'
                        }`}
                      >
                        <td className="px-2 py-1">
                          {it.primary_image_url ? (
                            <img
                              src={it.primary_image_url}
                              alt=""
                              loading="lazy"
                              className="h-8 w-8 object-cover border border-[var(--line-warm)]"
                            />
                          ) : (
                            <div className="h-8 w-8 bg-[var(--surface-header)] border border-[var(--line-warm)]" />
                          )}
                        </td>
                        <td className="px-2 py-1 text-[var(--text-primary)]">
                          {it.name_en || '—'}
                          {it.end_of_life && (
                            <span className="ml-1 text-[10px] text-[var(--status-rejected-fg)]">(EOL)</span>
                          )}
                        </td>
                        <td className="px-2 py-1 whitespace-nowrap tabular-nums">{it.sku}</td>
                        <td className="px-2 py-1 whitespace-nowrap">{variant || '—'}</td>
                        <td className="px-2 py-1 whitespace-nowrap tabular-nums text-[var(--mute)]">{it.ean || '—'}</td>
                        <td className="px-2 py-1 text-right tabular-nums">{money(it.price_cents)}</td>
                        <td className="px-2 py-1 text-right tabular-nums">
                          <span
                            data-testid={`kh-stock-${it.sku}`}
                            className={`${PILL} tabular-nums ${stockClasses(it.stock_available)}`}
                          >
                            {it.stock_available}
                          </span>
                        </td>
                        <td className="px-2 py-1 text-center">{it.season || '—'}</td>
                        <td className="px-2 py-1 text-center">
                          {it.in_shopify ? (
                            <span className="inline-flex items-center gap-0.5 text-[var(--status-approved-fg)]">
                              <Check className="h-3 w-3" /> Yes
                            </span>
                          ) : (
                            <span className="text-[var(--mute)]">No</span>
                          )}
                        </td>
                        <td className="px-2 py-1 text-center">
                          {it.in_shopify ? (
                            <span className="text-[var(--mute)]">—</span>
                          ) : (
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-6 text-xs"
                              disabled={rowPending}
                              onClick={() => handleAdd(it.sku)}
                            >
                              <Plus className="mr-1 h-3 w-3" />
                              {rowPending ? 'Adding…' : 'Add to Shopify'}
                            </Button>
                          )}
                        </td>
                      </tr>
                    )
                  })
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        <div className="flex items-center gap-2 mt-3">
          <span className="text-xs text-[var(--mute)]">Page {page} of {totalPages}</span>
          <div className="flex-1" />
          <Button
            size="sm"
            variant="outline"
            className="h-7 text-xs"
            disabled={page <= 1 || isFetching}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            <ChevronLeft className="mr-1 h-3 w-3" />
            Prev
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="h-7 text-xs"
            disabled={page >= totalPages || isFetching}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          >
            Next
            <ChevronRight className="ml-1 h-3 w-3" />
          </Button>
        </div>
      </div>
    </div>
  )
}
